import * as React from 'react';
import './kucoin-history.scss';
import { Page } from 'components/page/page';
import { Card } from 'components/card/card';
import { Table } from 'components/table/table';
import { CopyOutlined, ExportOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getAllKuCoinHistory } from 'core/repositories/kucoin-history';
import { Button, buttonType } from 'components/button/button';
import { Input } from 'components/input/input';
// import { ClientSidePagination } from "components/pagination/pagination";
import { Enum, PaginationList } from '../../../../core/domain/commons/common';
import moment from 'jalali-moment';
import { getPageNum } from 'core/utils/paggingHelper';
import { copyToClipboard } from 'core/utils/copyToClipboard';
import DatePicker from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import { ReactComponent as CalendarIcon } from 'assets/icons/dash-line-icons/calendar.svg';
import { faNumToEnNum } from 'core/utils/decimalExtentions';
import { downloadCSV } from '../../../../core/utils/export-to-file';
import { digitSperator } from 'core/utils/decimalExtentions';
import { jFormat } from 'core/utils/jFormat';
import { getViaAuth, responseValidator } from 'apis/api';
import { API } from 'apis';
import { Dropdown } from 'components/dropdown/dropdown';
import { KuCoinHistory } from 'core/domain/kucoin-history/kucoin-history';
import { Modal, Row, Col, Typography, Divider } from 'antd';

interface FilterValues {
    search: string;
}

const empty_filters = {
    search: '',
};

export const KuCoinHistoryPage: React.FC = () => {
    const [kuCoinHistory, setKuCoinHistory] = React.useState<PaginationList<KuCoinHistory>>();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [buttonLoading, setButtonLoading] = React.useState<boolean>(false);
    const [query, setQuery] = React.useState<string>('');
    const [deleteButtonLoading, setDeleteButtonLoading] = React.useState<boolean>(false);
    const [filterValues, setFilterValues] = React.useState<FilterValues>(empty_filters);
    const [pageSize, setPageSize] = React.useState<number>(10);
    const [transactionModalOpen, setTransactionModalOpen] = React.useState<boolean>(false);
    const [selectedTransaction, setSelectedTransaction] = React.useState<KuCoinHistory['transaction'] | null>(null);

    const hasActiveFilter = !!filterValues.search;

    const navigate = useNavigate();
    const startDateRef = React.useRef(null);
    const endDateRef = React.useRef(null);

    const openTransactionModal = (trx: KuCoinHistory['transaction']) => {
        setSelectedTransaction(trx);
        setTransactionModalOpen(true);
    };

    const filterHandle = () => {
        let _query = '';

        if (filterValues.search) {
            _query += `${_query.includes('?') ? '&' : '?'}keywords=${filterValues.search}`;
        }

        setQuery(_query);

        const loader = _query ? setButtonLoading : setLoading;

        getAllKuCoinHistory(loader, setKuCoinHistory, getPageNum(), pageSize, _query);
    };

    const clearFiltersHandle = () => {
        setFilterValues(empty_filters);
        setQuery('');

        getAllKuCoinHistory(setDeleteButtonLoading, setKuCoinHistory, getPageNum(), pageSize, '');
    };

    React.useEffect(() => {
        setLoading(true);
        getAllKuCoinHistory(setLoading, setKuCoinHistory, getPageNum(), pageSize);
    }, [pageSize]);

    const shortenTxid = (txid?: string) => {
        if (!txid) return '';
        if (txid.length <= 20) return txid;
        return `${txid.slice(0, 10)}...${txid.slice(-6)}`;
    };

    const columns: any[] = [
        {
            name: '#',
            id: 'index',
            width: '4%',
            selector: (_row: KuCoinHistory, index: number) => index + 1,
        },
        {
            name: 'نام و نام خانوادگی',
            id: 'fullName',
            width: '16%',
            selector: (row: KuCoinHistory) =>
                row.transaction
                    ? row.transaction.createdBy.fullName ||
                      `${row.transaction.createdBy.firstName || ''} ${
                          row.transaction.createdBy.lastName || ''
                      }`.trim() ||
                      row.transaction.createdBy.userName ||
                      '-'
                    : '-',
        },
        {
            name: 'ارز / مقدار',
            id: 'assetQuantity',
            width: '14%',
            selector: (row: KuCoinHistory) => `${row.asset} - ${row.quantity}`,
            cell: (row: KuCoinHistory) => (
                <div className="asset-quantity-cell">
                    <span className="quantity en">{digitSperator(row.quantity)}</span> /{' '}
                    <b className="asset en">{row.asset}</b>
                </div>
            ),
        },
        {
            name: 'کد پیگیری',
            id: 'trackingCode',
            width: '16%',
            selector: (row: KuCoinHistory) => row.transaction?.trackingCode,
            cell: (row: KuCoinHistory) => (
                <span className="en copiable tracking-code-cell">
                    <span title={row.transaction?.trackingCode || ''}>{row.transaction?.trackingCode || '-'}</span>
                    {row.transaction?.trackingCode && (
                        <CopyOutlined
                            onClick={() => {
                                copyToClipboard(row.transaction!.trackingCode, 'کد پیگیری');
                            }}
                        />
                    )}
                </span>
            ),
        },
        {
            name: 'وضعیت',
            id: 'state',
            width: '8%',
            selector: (row: KuCoinHistory) => row.transaction?.state?.title,
            cell: (row: KuCoinHistory) => <span>{row.transaction?.state?.title}</span>,
        },
        {
            name: 'هش تراکنش',
            id: 'txid',
            width: '18%',
            selector: (row: KuCoinHistory) => row.txid,
            cell: (row: KuCoinHistory) => (
                <span className="en copiable txid-cell">
                    <span title={row.txid}>{shortenTxid(row.txid)}</span>
                    {row.txid && (
                        <CopyOutlined
                            onClick={() => {
                                copyToClipboard(row.txid, 'هش');
                            }}
                        />
                    )}
                </span>
            ),
        },
        {
            name: 'شبکه',
            id: 'network',
            width: '8%',
            selector: (row: KuCoinHistory) => row.network,
            cell: (row: KuCoinHistory) => <span>{row.network ? row.network.toUpperCase() : '-'}</span>,
        },
        {
            name: 'تاریخ / ساعت',
            id: 'createTime',
            width: '8%',
            selector: (row: KuCoinHistory) => row.createTime,
            cell: (row: KuCoinHistory) => (
                <div
                    className="column-date"
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
                >
                    <span className="day">
                        {row.createTime ? moment.utc(row.createTime).locale('fa').local().format('YYYY/MM/DD') : ''}
                    </span>
                    <span className="time">
                        {row.createTime ? moment.utc(row.createTime).locale('fa').local().format('HH:mm') : ''}
                    </span>
                </div>
            ),
        },
        {
            name: 'تراکنش مرتبط',
            id: 'transaction',
            width: '8%',
            selector: (row: KuCoinHistory) => row.transaction,
            cell: (row: KuCoinHistory) => {
                const trx = row.transaction;
                if (!trx) {
                    return <span>شارژ نشده</span>;
                }

                return (
                    <InfoCircleOutlined
                        className="transaction-info-icon"
                        onClick={() => openTransactionModal(trx)}
                        style={{ marginRight: 8 }}
                    />
                );
            },
        },
    ];

    return (
        <Page className="pmx-kucoin-history" title="تاریخچه کوکوین">
            <Card title="تاریخچه کوکوین" blur={loading}>
                <div className="filter-row">
                    <div className="fields">
                        <Input
                            value={filterValues.search}
                            className="search"
                            placeholder="جستجو کنید ..."
                            onPressEnter={filterHandle}
                            name="search"
                            onChange={(e) => setFilterValues({ ...filterValues, search: e.target.value })}
                        />
                    </div>
                    <div className="buttons">
                        <Button
                            loading={buttonLoading}
                            disabled={buttonLoading}
                            text="اعمال فیلتر"
                            type={buttonType.success}
                            onClick={() => filterHandle()}
                        />
                        <Button
                            loading={deleteButtonLoading}
                            disabled={deleteButtonLoading || !hasActiveFilter}
                            text="حذف فیلتر"
                            type={buttonType.error}
                            onClick={clearFiltersHandle}
                        />
                        <Button
                            text="خروجی اکسل"
                            type={buttonType.info}
                            icon={<ExportOutlined />}
                            disabled={!kuCoinHistory?.items || kuCoinHistory.items.length === 0}
                            onClick={() => {
                                if (kuCoinHistory?.items && kuCoinHistory.items.length > 0) {
                                    downloadCSV('تاریخچه کوکوین', kuCoinHistory.items);
                                }
                            }}
                        />
                    </div>
                </div>
                {kuCoinHistory?.items && kuCoinHistory?.items.length > 0 && (
                    <>
                        <Table
                            setPageSize={setPageSize}
                            blur={loading}
                            setloader={setLoading}
                            getdata={getAllKuCoinHistory}
                            setdata={setKuCoinHistory}
                            pagination={kuCoinHistory.pagination}
                            className="kucoin-history-table"
                            data={kuCoinHistory.items}
                            column={columns}
                            filters={query}
                            conditionalRowStyles={[
                                {
                                    when: (row: KuCoinHistory) => !row.transaction,
                                    style: {
                                        backgroundColor: '#fac89861',
                                    },
                                },
                            ]}
                        />
                    </>
                )}
                <Modal
                    open={transactionModalOpen}
                    onCancel={() => setTransactionModalOpen(false)}
                    footer={null}
                    title="جزئیات تراکنش"
                    destroyOnClose
                >
                    {selectedTransaction && (
                        <>
                            <Row gutter={[16, 12]}>
                                {selectedTransaction.trackingCode && (
                                    <Col span={12}>
                                        <Typography.Text type="secondary">کد پیگیری</Typography.Text>
                                        <div className="en">{selectedTransaction.trackingCode}</div>
                                    </Col>
                                )}
                                {selectedTransaction.amount != null && (
                                    <Col span={12}>
                                        <Typography.Text type="secondary">مبلغ</Typography.Text>
                                        <div className="en">{digitSperator(selectedTransaction.amount)}</div>
                                    </Col>
                                )}
                                {selectedTransaction.createdBy && (
                                    <Col span={12}>
                                        <Typography.Text type="secondary">ایجاد کننده</Typography.Text>
                                        <div>
                                            {selectedTransaction.createdBy.fullName ||
                                                `${selectedTransaction.createdBy.firstName || ''} ${
                                                    selectedTransaction.createdBy.lastName || ''
                                                }`.trim() ||
                                                selectedTransaction.createdBy.userName}
                                        </div>
                                    </Col>
                                )}
                                {selectedTransaction.createdOn && (
                                    <Col span={12}>
                                        <Typography.Text type="secondary">تاریخ ایجاد</Typography.Text>
                                        <div>
                                            {moment
                                                .utc(selectedTransaction.createdOn)
                                                .locale('fa')
                                                .local()
                                                .format('YYYY/MM/DD HH:mm')}
                                        </div>
                                    </Col>
                                )}
                                {selectedTransaction.network && (
                                    <>
                                        {(selectedTransaction.network.name || selectedTransaction.network.network) && (
                                            <Col span={12}>
                                                <Typography.Text type="secondary">شبکه</Typography.Text>
                                                <div>
                                                    {selectedTransaction.network.name ||
                                                        selectedTransaction.network.network}
                                                </div>
                                            </Col>
                                        )}
                                        {selectedTransaction.network.fee != null && (
                                            <Col span={12}>
                                                <Typography.Text type="secondary">کارمزد شبکه</Typography.Text>
                                                <div className="en">
                                                    {digitSperator(selectedTransaction.network.fee)}
                                                </div>
                                            </Col>
                                        )}
                                    </>
                                )}
                                {selectedTransaction.txId && (
                                    <Col span={24}>
                                        <Typography.Text type="secondary">TxID</Typography.Text>
                                        <div className="en copiable">
                                            <span title={selectedTransaction.txId}>
                                                {shortenTxid(selectedTransaction.txId)}
                                            </span>
                                            <CopyOutlined
                                                onClick={() => copyToClipboard(selectedTransaction.txId, 'TxID')}
                                            />
                                        </div>
                                    </Col>
                                )}
                                {selectedTransaction.description && (
                                    <Col span={24}>
                                        <Typography.Text type="secondary">توضیحات</Typography.Text>
                                        <div>{selectedTransaction.description}</div>
                                    </Col>
                                )}
                            </Row>
                        </>
                    )}
                </Modal>
            </Card>
        </Page>
    );
};
